import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { fetchAuthSession, getCurrentUser, signOut } from "../utils/session";
import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { matrixRoleOf, roleCan } from "../utils/frontDeskMatrix";
import { useUserPermissions } from "../hooks/useUserPermissions";
import { styled } from "styletron-react";
import { AppHeader } from "../components/AppHeader";
import { X } from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { PhoneMenuPanel } from "../components/phone/PhoneMenuPanel";
import { Contacts } from "./Contacts";
import { Dashboard } from "./Dashboard";
import { Invitations } from "./invitation/Invitations";
import { AccessPage } from "./invitation/AccessPage";
import { Payments } from "./Payments";
import LoginPage from "./Login";
import { toaster } from "baseui/toast";
import { useBusinessContext } from "../context/BusinessContext";
import { Spinner } from "baseui/spinner";
import { BusinessSelectionModal } from "../components/BusinessSelectionModal";
import { EstatesExplorer } from "./Estates/EstatesExplorer";
import { EstateGroupDetails } from "./Estates/[groupId]";
import { PropertyAdmin } from "./PropertyAdmin";
import { Users } from "./Users";
import { UsersRoute } from "./UsersRoute";
import { UserDetail } from "./UserDetail";
import { UsersDemo } from "./UsersDemo/index";
import { TenantSettings } from "./settings/TenantSettings";
import { MerchantConfig } from "./settings/MerchantConfig";
import { Settings } from "./settings/index";
import { Roles } from "./settings/Roles";
import { GatekeeperUsers } from "./settings/GatekeeperUsers";
import { GatesPage } from "./settings/gates/GatesPage";
import { GateFormPage } from "./settings/gates/GateFormPage";
import { ZonesPage } from "./settings/zones/ZonesPage";
import { ZoneFormPage } from "./settings/zones/ZoneFormPage";
import { ZONES_ENABLED } from "./settings/zones/zonesModel";
import { InvitationTypes } from "./settings/InvitationTypes";
import { PermissionGroups } from "./settings/PermissionGroups";
import { PushNotifications } from "./settings/PushNotifications";
import { PushNotificationCompose } from "./settings/PushNotificationCompose";
import { NotificationTemplates } from "./settings/NotificationTemplates";
import { Products } from "./Products";
import { Services } from "./Services";
import { ServiceEditorPage } from "./ServiceEditor/ServiceEditorPage";
import { BusinessPage } from "./BusinessPage/BusinessPage";
import { NewBusinessPage } from "./BusinessPage/NewBusinessPage";
import { ActivityHome } from "./Bookings/ActivityHome";

import { CMS } from "./CMS";
import { Ads } from "./settings/Ads";
import { ArticleEditorPage } from "./cms/ArticleEditorPage";
import { ArticlePreviewPage } from "./cms/ArticlePreviewPage";
import { AdEditorPage } from "./cms/AdEditorPage";
import { AdsPreviewPage } from "./cms/AdsPreviewPage";
import { AppHomePage } from "./cms/AppHomePage";
import { EventsList } from "./events/EventsList";
import { EventEditorPage } from "./events/EventEditorPage";
import { EventPreviewPage } from "./events/EventPreviewPage";
import { AccessSettings } from "./events/AccessSettings";
import { EventDashboard } from "./events/EventDashboard";
import { BusinessList } from "./BusinessList";
import { PublicEvents } from "./public/PublicEvents";
import { PublicEventDetail } from "./public/PublicEventDetail";
import { PublicLangProvider } from "../i18n/publicEvents";
import { PublicInvitation } from "./public/PublicInvitation";
import { PrivacyPolicy } from "./public/PrivacyPolicy";
import { TermsOfService } from "./public/TermsOfService";
import { RequestDeleteAccount } from "./public/RequestDeleteAccount";
import { PayLinkPage } from "./public/payLink/PayLinkPage";
import { BusinessRequests } from "./support/BusinessRequests";
import { RegisterRoutes } from "./register/RegisterBusiness";
import { BusinessRequestDetail } from "./support/BusinessRequestDetail";
import axios from "axios";
import { clearAllStorage, endSession, forceAuthReset } from "../utils/storageUtils";
import { isTokenExpired } from "../utils/token";
import { refreshToken } from "../services/authService";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { Sheet } from "../components/ui/Sheet";
import { PhoneAccessGate } from "../components/phone/PhoneAccessGate";
import { useLocation } from "react-router-dom";
import { canSignInWithoutBusiness } from '../utils/sessionAccess';

const Layout = styled("div", ({ $sidebarWidth }: { $sidebarWidth?: number }) => ({
  display: "grid",
  // SAM-944: minmax(0, 1fr), not 1fr — a plain 1fr track grows to its content's min width, so a wide page
  // widened the whole layout past the phone and `overflow: hidden` below left it unreachable.
  gridTemplateColumns: `${$sidebarWidth || 240}px minmax(0, 1fr)`,
  // Without an explicit row track, the implicit "auto" row sizes to its tallest child's
  // *content* height instead of stretching to Layout's own (already-constrained) height — so a
  // route whose content is taller than the viewport (e.g. ServiceEditorPage's `h-full` shell)
  // grows past 100vh instead of scrolling internally, and anything pinned after it (a sticky
  // footer/save bar) gets clipped by `overflow: hidden` below with no way to reach it. `minmax(0,
  // 1fr)` caps the row at the available height and lets percentage-height descendants resolve
  // correctly, restoring the intended nested-scroll behavior.
  gridTemplateRows: "minmax(0, 1fr)",
  minHeight: 0, // important for nested scrolling
  overflow: "hidden",
  transition: "grid-template-columns 0.25s ease",
  '@media (max-width: 768px)': {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
}));

const DesktopSidebar = styled("div", {
  display: 'block',
  '@media (max-width: 768px)': {
    display: 'none',
  },
});

const LoadingContainer = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "calc(100vh - 77px)",
});

const DashboardGate = () => {
  const permissions = useUserPermissions();
  const { isInvitationOnly, isPropertyManager, isAdmin, isBusinessAdmin, businessId, isEventDashboardOnly, isLoading } = permissions;
  const matrixRole = matrixRoleOf(permissions);
  const { pathname } = useLocation();
  if (isLoading) return null;
  // S-16: the dashboard is tenant-wide; a business admin lands on their own business.
  // SAM-1321: the business that administers an estate group is an estate group role first: it lands on its matrix's screens.
  if (isBusinessAdmin && !isAdmin && !matrixRole) return <Navigate to={businessId ? `/business/${businessId}` : '/business/list'} replace />;
  if (isEventDashboardOnly) return <Navigate to="/eventsad/list" replace />;
  // The tenant Front Desk and the estate group roles see the dashboard (their matrix) but still land on Passes.
  if (matrixRole && roleCan(matrixRole, 'dashboard', 'view')) return pathname === '/' ? <Navigate to="/invitations" replace /> : <Dashboard />;
  if (matrixRole && matrixRole !== 'tenant_front_desk') return <Navigate to="/invitations" replace />;
  if (isInvitationOnly) return <Navigate to="/invitations" replace />;
  if (isPropertyManager && !isAdmin) return <Navigate to="/property-admin" replace />;
  return <Dashboard />;
};

// Export the Content component so it can be imported by other components
export const Content = styled("div", {
  padding: "24px",
  overflow: "auto",
  background: "#F7F7F7",
  width: "100%",
  boxSizing: "border-box",
  height: "100%",
});

// New component specifically for pages with tables (no padding, flex layout)
export const TableContent = styled("div", {
  background: "#fff",
  width: "100%",
  boxSizing: "border-box",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden", // Prevent scrolling on the container itself
});

const ContentWithChildren = ({ children }: { children: React.ReactNode }) => {
  return <div style={{
    padding: "24px",
    overflow: "auto",
    background: "#F7F7F7",
    width: "100%",
    boxSizing: "border-box",
    height: "100%",
  }}>{children}</div>;
};

const checkMultipleBusinesses = (userBusinessIds: string): boolean => {
  if (!userBusinessIds) return false;
  const businessIds = userBusinessIds.split(',');
  const hasMultiple = businessIds.length > 1;
  return hasMultiple;
};

interface Business {
  id: string;
  name: string;
  logo_url?: string;
}

// Separate component for public routes (no auth needed)
const PublicRoutes: React.FC = () => (
  <PublicLangProvider>
  <Routes>
    <Route path="/events/:eventId" element={<PublicEventDetail />} />
    <Route path="/events" element={<PublicEvents />} />
    {/* Legacy routes with tenantId in URL */}
    <Route path="/events/:tenantId/:eventId" element={<PublicEventDetail />} />
  </Routes>
  </PublicLangProvider>
);

// SAM-633: public pay page for a booking payment link — /pay/{43-char token}. No auth, no
// sidebar, no business context; its API calls use their own client (api/paymentLinkApi.ts) so a
// signed-in admin's bearer never reaches the public payment routes.
const PublicPayRoutes: React.FC = () => (
  <PublicLangProvider>
  <Routes>
    <Route path="/pay/:token" element={<PayLinkPage />} />
  </Routes>
  </PublicLangProvider>
);

// Hash route handler for legacy Flutter web links (/#/invitation/{id}, /#/access/{id}, /#/event/{id})
// Also handles the public privacy policy page (/#/privacy_policy)
const HashRouteHandler: React.FC = () => {
  const [hashPath, setHashPath] = React.useState(window.location.hash);

  React.useEffect(() => {
    const onHashChange = () => setHashPath(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Privacy policy: #/privacy_policy
  if (/^#\/privacy_policy\/?$/.test(hashPath)) {
    return <PrivacyPolicy />;
  }

  // Terms of service: #/terms or #/terms_of_service
  if (/^#\/(terms|terms_of_service)\/?$/.test(hashPath)) {
    return <TermsOfService />;
  }

  // Parse hash: #/invitation/{id} or #/access/{id} or #/event/{id}
  const match = hashPath.match(/^#\/(invitation|access|event)\/(.+)$/);
  if (!match) return null;

  const [, type, id] = match;
  return <PublicInvitation type={type as 'invitation' | 'access' | 'event'} id={id} />;
};



export const LayoutPage = () => {
  const location = useLocation();

  // Hash routes — /#/invitation/{id}, /#/access/{id}, /#/event/{id},
  // /#/privacy_policy, /#/terms (or /#/terms_of_service)
  const hashMatch = window.location.hash.match(
    /^#\/(invitation|access|event|privacy_policy|terms|terms_of_service)/,
  );
  if (hashMatch) {
    return <HashRouteHandler />;
  }

  // Public event routes — /events or /events/:id (but not /eventsad or other paths)
  if (location.pathname === '/events' || /^\/events\/[^a]/.test(location.pathname)) {
    return <PublicRoutes />;
  }

  // Public pay page for a booking payment link — /pay/:token (SAM-633).
  if (/^\/pay\/[^/]+\/?$/.test(location.pathname)) {
    return <PublicPayRoutes />;
  }

  // SAM-1298 (W2–W4): «Register your business» — a business owner creates the account and sends the request. No menu.
  if (location.pathname === '/register' || location.pathname.startsWith('/register/')) {
    return <RegisterRoutes />;
  }

  // Public account-deletion request page — /requestdelete. This URL is declared
  // in the Google Play Data Safety form, so it must render without auth.
  if (location.pathname === '/requestdelete' || location.pathname === '/requestdelete/') {
    return <RequestDeleteAccount />;
  }


  return <AuthenticatedLayout />;
};

const AuthenticatedLayout = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null); // null = loading
  const [authError, setAuthError] = useState<string | null>(null);
  const { refreshBusiness, loading: businessLoading, isSidebarCollapsed } = useBusinessContext();
  const [showBusinessSelection, setShowBusinessSelection] = useState(false);
  const [availableBusinesses, setAvailableBusinesses] = useState<Business[]>([]);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // SAM-944: any navigation closes the menu drawer, whichever control caused it (a link, back, a redirect),
  // so its overlay never stays over the new page.
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  // SAM-169: close the mobile menu drawer when the viewport grows back to
  // desktop — otherwise its overlay stays stuck over the page after a resize.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e: MediaQueryListEvent) => {
      if (!e.matches) setIsMobileSidebarOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // SAM-240: httpClient/businessAccessClient (401 responses) and checkAuth
  // below (expired token + failed refresh) all land here via a full reload to
  // "/?session_expired=1" after wiping storage. Surface an explicit message
  // instead of a silent redirect, then strip the query param so it doesn't
  // linger in the URL or resurface the message on a later manual refresh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === '1') {
      const message = 'Your session has expired. Please log in again.';
      setAuthError(message);
      toaster.negative(message, { autoHideDuration: 5000 });
      params.delete('session_expired');
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`
      );
    }
  }, []);

  const fetchAvailableBusinesses = async (businessIds: string[], token: string): Promise<Business[]> => {
    try {
      
      // Make request to general business endpoint
      const response = await axios({
        url: `${import.meta.env.VITE_API_BASE_URL}/crud/business`,
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.data && response.data.data.length > 0) {
        const allBusinesses = response.data.data as Business[]; 
        
        // Filter businesses that match the user's business IDs
        const userBusinesses = allBusinesses.filter(business => 
          businessIds.includes(business.id)
        );
        
        return userBusinesses;
      }
      return [];
    } catch (error) {
      console.error('Error fetching business data:', error);
      return [];
    }
  };

  const showBusinessSelectionIfNeeded = async (userBusinessIds: string, token: string): Promise<boolean> => {
    
    // Primero verificar si ya hay un negocio seleccionado en sessionStorage
    const selectedBusinessId = sessionStorage.getItem('selectedBusinessId');
    
    if (selectedBusinessId) {
      return false; // Ya hay un negocio seleccionado, no mostrar el modal
    }
    
    if (checkMultipleBusinesses(userBusinessIds)) {
      const businessIds = userBusinessIds.split(',').map(id => id.trim());
      
      const businesses = await fetchAvailableBusinesses(businessIds, token);
      
      if (businesses.length > 1) {
        setAvailableBusinesses(businesses);
        setShowBusinessSelection(true);
        return true;
      } else if (businesses.length === 1) {
        // Just one business available
        sessionStorage.setItem('selectedBusinessId', businesses[0].id);
      }
    }
    return false;
  };
  
  const handleBusinessSelect = (businessId: string) => {
    sessionStorage.setItem('selectedBusinessId', businessId);
    setShowBusinessSelection(false);
    refreshBusiness();
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Verificar si hay token en localstorage
        const token = localStorage.getItem('token');
        
        if (!token) {
          setIsAuthenticated(false);
          return;
        }

        // SAM-148/SAM-240: an expired token must never reach the app shell — the
        // request queue would wait forever for business data. Try a silent
        // refresh; if the refresh token has also expired, do a full clean
        // reload into the login screen (with an explicit "session expired"
        // message) instead of just flipping React state. A hard reload
        // guarantees every module-level flag (isBusinessDataLoaded in both
        // httpClient and businessAccessClient, their request queues, and any
        // stale BusinessContext state) resets, rather than leaving the SPA in
        // an intermediate state where a stray leftover could block the very
        // next login attempt.
        if (isTokenExpired(token)) {
          const refreshed = await refreshToken().catch(() => false);
          if (!refreshed) {
            clearAllStorage();
            window.location.href = "/?session_expired=1";
            return;
          }
        }

        // Verificar si el token es válido
        try {
          const user = await getCurrentUser();
          const session = await fetchAuthSession();
          
          // Validar que el token sea válido
          if (session.tokens?.idToken) {
            const freshToken = session.tokens.idToken.toString();
            localStorage.setItem("token", freshToken);
            
            const tokenParts = freshToken.split('.');
            if (tokenParts.length === 3) {
              try {
                const claims = JSON.parse(atob(tokenParts[1]));

                const userBusinessIds = claims['custom:business_id'];
                const userGroups: string[] = claims['cognito:groups'] || [];
                const noBusinessRole = canSignInWithoutBusiness(userGroups);

                // SAM-1298: an account with no group at all is a business owner whose request is not approved yet: its
                // request (or the form to send one) is at /register/business.
                if (!userBusinessIds && userGroups.length === 0) {
                  window.location.assign('/register/business');
                  return;
                }

                if (!userBusinessIds && !noBusinessRole) {
                  setAuthError("User is not associated with any business");
                  toaster.negative("User is not associated with any business", {
                    autoHideDuration: 5000,
                  });
                  handleLogout();
                  return;
                }

                if (userBusinessIds) {
                  // Regular users: show business selection if multiple businesses
                  await showBusinessSelectionIfNeeded(userBusinessIds, freshToken);
                }
                // Property managers (assigned estates) and the tenant Front Desk have no business_id

                setIsAuthenticated(true);
                setAuthError(null);

                // Also for roles without a business (property manager, tenant Front Desk): this is what
                // releases the API request queue, which otherwise waits forever for business data.
                await refreshBusiness();
                return;
              } catch (e) {
                console.error("Error parsing JWT:", e);
                setAuthError("Authentication failed: Invalid token");
                handleLogout();
              }
            } else {
              setAuthError("Authentication failed: Malformed token");
              handleLogout();
            }
          } else {
            setAuthError("Authentication failed: No token provided");
            handleLogout();
          }
        } catch (error) {
          console.error('Error verificando autenticación:', error);
          
          // Check if it's the "already signed in user" error
          const errorMsg = error instanceof Error ? error.message : "";
          if (errorMsg.includes("There is already a signed in user")) {
            clearAllStorage();
            toaster.negative("Authentication conflict resolved. Please log in again.", {
              autoHideDuration: 5000,
            });
          }
          
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Error checking auth state:", error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  // SAM-377: checkAuth above only runs once on mount, so a token that expires
  // while the tab sits open and idle is never noticed until some later request
  // gets a 401 (httpClient/businessAccessClient) — the panel keeps rendering as
  // if the session were still valid. Re-run the same expired-token check (try a
  // silent refresh, and on failure do the SAM-240 full-reload-to-login-with-
  // message flow) whenever the tab regains focus/visibility, which is exactly
  // the moment described in the bug report ("volver a la pestaña"). This is
  // deliberately event-driven rather than a poll or a scheduled setTimeout for
  // the exact exp instant: a background timer would need to survive tab
  // sleep/throttling and re-arm on every token refresh, while a `focus`/
  // `visibilitychange` check is a few lines, fires exactly when the user is
  // about to interact again, and reuses logic that's already proven to work.
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkExpiryOnResume = async () => {
      if (document.visibilityState !== 'visible') return;

      const token = localStorage.getItem('token');
      if (!token || !isTokenExpired(token)) return;

      const refreshed = await refreshToken().catch(() => false);
      if (!refreshed) {
        clearAllStorage();
        window.location.href = "/?session_expired=1";
      }
    };

    document.addEventListener('visibilitychange', checkExpiryOnResume);
    window.addEventListener('focus', checkExpiryOnResume);
    return () => {
      document.removeEventListener('visibilitychange', checkExpiryOnResume);
      window.removeEventListener('focus', checkExpiryOnResume);
    };
  }, [isAuthenticated]);

  const handleSignIn = async () => {
    try {
      setIsAuthenticated(null); // Poner en modo carga durante el login
      
      let user = await getCurrentUser();
      const session = await fetchAuthSession();
      
      if (session.tokens?.idToken) {
        const token = session.tokens.idToken.toString();
        localStorage.setItem("token", token);
        
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          try {
            const claims = JSON.parse(atob(tokenParts[1]));

            const userBusinessIds = claims['custom:business_id'];
            const userGroups: string[] = claims['cognito:groups'] || [];
            const noBusinessRole = canSignInWithoutBusiness(userGroups);

            // SAM-1298: an account with no group at all is a business owner whose request is not approved yet: its
            // request (or the form to send one) is at /register/business.
            if (!userBusinessIds && userGroups.length === 0) {
              window.location.assign('/register/business');
              return;
            }

            if (!userBusinessIds && !noBusinessRole) {
              setAuthError("User is not associated with any business");
              toaster.negative("User is not associated with any business", {
                autoHideDuration: 5000,
              });
              handleLogout();
              return;
            }

            if (userBusinessIds) {
              // Regular users: show business selection if multiple businesses
              const hasMultipleBusinesses = await showBusinessSelectionIfNeeded(userBusinessIds, token);
              setIsAuthenticated(!!user);
              setAuthError(null);
              if (!hasMultipleBusinesses) {
                await refreshBusiness();
              }
            } else {
              // Property managers and the tenant Front Desk: no business selection needed, but the
              // request queue still has to be released (see refreshBusiness above).
              setIsAuthenticated(!!user);
              setAuthError(null);
              await refreshBusiness();
            }

            return;
          } catch (e) {
            console.error("Error parsing JWT:", e);
            setAuthError("Authentication failed: Invalid token");
            handleLogout();
            return;
          }
        } else {
          // Token malformado, no autenticar
          setAuthError("Authentication failed: Malformed token");
          handleLogout();
          return;
        }
      } else {
        // No hay token, no autenticar
        setAuthError("Authentication failed: No token provided");
        handleLogout();
        return;
      }
    } catch (error) {
      console.error("Error during sign in:", error);
      
      // Check if it's the "already signed in user" error
      const errorMsg = error instanceof Error ? error.message : "";
      if (errorMsg.includes("There is already a signed in user")) {
        clearAllStorage();
        setAuthError("Authentication conflict resolved. Please try logging in again.");
        toaster.negative("Authentication conflict resolved. Please try logging in again.", {
          autoHideDuration: 5000,
        });
      } else {
        setAuthError("Authentication failed");
      }
      
      handleLogout();
    }
  };

  const handleLogout = () => {
    // SAM-1320: wipes storage and reloads, so nothing of this session (business, caches) reaches the next login
    void endSession(signOut);
  };

  // Mostrar loading mientras se verifica el estado de autenticación
  if (isAuthenticated === null) {
    return (
      <>
        <LoadingContainer>
          <Spinner $size="large" />
        </LoadingContainer>
      </>
    );
  }

  // Mostrar loading mientras se cargan los datos del negocio (pero ya autenticado)
  if (isAuthenticated && businessLoading) {
    return (
      <>
        <AppHeader onLogOut={handleLogout} isAuthenticated={true} />
        <LoadingContainer>
          <Spinner $size="large" />
        </LoadingContainer>
      </>
    );
  }

  return (
    <div className={isAuthenticated ? "app" : ""}>
      {isAuthenticated && <AppHeader onLogOut={handleLogout} isAuthenticated={isAuthenticated} onMenuClick={() => setIsMobileSidebarOpen(true)} />}
      
      {/* Modal de selección de negocio - La movemos fuera de la condición de autenticación */}
      <BusinessSelectionModal 
        isOpen={showBusinessSelection}
        onClose={() => {
          // Si se cierra sin seleccionar, usar el primer negocio por defecto
          if (availableBusinesses.length > 0) {
            handleBusinessSelect(availableBusinesses[0].id);
          }
          setShowBusinessSelection(false);
        }}
        businesses={availableBusinesses}
        onBusinessSelect={(businessId) => {
          handleBusinessSelect(businessId);
        }}
      />
      
      {isAuthenticated ? (
        <>
          {/* Mobile Sidebar Drawer */}
          <Sheet
            open={isMobileSidebarOpen}
            onOpenChange={setIsMobileSidebarOpen}
            side="left"
            width={300}
            title="Main menu"
          >
            <div className="flex h-full w-full min-w-0 flex-col">
              <div className="flex shrink-0 justify-end px-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsMobileSidebarOpen(false)}
                  aria-label="Close menu"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg border-0 bg-transparent text-[var(--ink-700)]"
                >
                  <X size={20} aria-hidden />
                </button>
              </div>
              {/* SAM-944 (canvas PhoneMenu): who is signed in, the entries and «Log out». */}
              <div className="flex min-h-0 flex-1">
                <PhoneMenuPanel onLogOut={() => { setIsMobileSidebarOpen(false); handleLogout(); }}>
                  <Sidebar variant="drawer" onNavigate={() => setIsMobileSidebarOpen(false)} />
                </PhoneMenuPanel>
              </div>
            </div>
          </Sheet>

          <Layout $sidebarWidth={isSidebarCollapsed ? 64 : 240}>
            <DesktopSidebar>
              <Sidebar />
            </DesktopSidebar>
          <PhoneAccessGate>
          <Routes>
            <Route path="/" element={<DashboardGate />} />
            <Route path="/dashboard" element={<DashboardGate />} />
            <Route
              path="/invitations"
              element={
                <ProtectedRoute requireInvitationManager fdSection="passes">
                  <TableContent>
                    <Invitations />
                  </TableContent>
                </ProtectedRoute>
              }
            />
              {/* SAM-199 — full-page Access flow, replacing the AddInvitation drawer. Not
                  wrapped in TableContent: like ServiceEditorPage, it manages its own
                  full-height shell (header + scroll area). */}
              <Route
                path="/invitations/new"
                element={
                  <ProtectedRoute requireInvitationManager fdSection="passes" fdAction="create">
                    <AccessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/business/list"
                element={
                  <ProtectedRoute requireBusinessAccess fdSection="business">
                    <BusinessList />
                  </ProtectedRoute>
                }
              />
              {/* SAM-198 (mock 7b) — "New business" is its own short form; the rest is set up on
                  the business page after creating it. Static path, so it wins over /business/:id. */}
              <Route
                path="/business/new"
                element={
                  <ProtectedRoute requireBusinessAccess fdSection="business" fdAction="create">
                    <NewBusinessPage />
                  </ProtectedRoute>
                }
              />
              {/* SAM-198 — full-page business view (replaces the list + side panel split).
                  Not wrapped in TableContent: it manages its own full-height shell. */}
              <Route
                path="/business/:id"
                element={
                  <ProtectedRoute requireBusinessAccess fdSection="business">
                    <BusinessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <ProtectedRoute requirePaymentsManager fdSection="transactions">
                    <Payments />
                  </ProtectedRoute>
                }
              />

            <Route path="/contacts" element={<Contacts />} />
            <Route 
                path="/products"
                element={
                  <ProtectedRoute requirePaymentsManager fdSection="business">
                    <Products />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/services"
                element={
                  <ProtectedRoute requireServicesManager fdSection="business">
                    <Services />
                  </ProtectedRoute>
                }
              />
              {/* SAM-198 — full-page service editor, replacing ServiceDetailDrawer. "/services/new"
                  and "/services/:serviceId" share one component: ServiceEditorPage treats a
                  missing/"new" serviceId as create mode. */}
              <Route
                path="/services/new"
                element={
                  <ProtectedRoute requireServicesManager fdSection="business" fdAction="create">
                    <ServiceEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/services/:serviceId"
                element={
                  <ProtectedRoute requireServicesManager fdSection="business">
                    <ServiceEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings"
                element={
                  <ProtectedRoute requireBookingsManager fdSection="bookings">
                    <ActivityHome />
                  </ProtectedRoute>
                }
              />
              {/* Arrivals is gone for every role (29 Oct): old links and bookmarks land on Activity. */}
              <Route path="/arrivals" element={<Navigate to="/bookings" replace />} />
            <Route
              path="/users"
              element={
                <ProtectedRoute requireUsersManager fdSection="users">
                  <UsersRoute />
                </ProtectedRoute>
              } 
            />
            {/* SAM-1307 (canvas C5): Estate Requests, the table that used to be a tab of Users, now under Support. */}
            <Route
              path="/support/estate-requests"
              element={
                <ProtectedRoute requireUsersManager fdSection="users">
                  <Users fixedTab="estate-requests" />
                </ProtectedRoute>
              }
            />
            {/* SAM-1298 (canvas S1–S7): Business Requests. The pages show each role its part (the Front Desk, carnets only). */}
            <Route
              path="/support/business-requests"
              element={
                <ProtectedRoute requireUsersManager fdSection="users">
                  <BusinessRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support/business-requests/:id"
              element={
                <ProtectedRoute requireUsersManager fdSection="users">
                  <BusinessRequestDetail />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/users/:userId" 
              element={
                <ProtectedRoute requireUsersManager fdSection="users">
                  <UserDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users-demo" 
              element={
                <ProtectedRoute requireAdmin>
                  <UsersDemo />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/estates" 
              element={
                <ProtectedRoute requireEstatesManager fdSection="estates">
                  <EstatesExplorer />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/estates/:groupId" 
              element={
                <ProtectedRoute requireEstatesManager fdSection="estates">
                  <EstateGroupDetails />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/property-admin"
              element={
                <ProtectedRoute requirePropertyManager>
                  <PropertyAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/home-screen"
              element={
                <ProtectedRoute requireCMSManager fdSection="cms">
                  <AppHomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/articles"
              element={
                <ProtectedRoute requireCMSManager fdSection="cms">
                  <CMS />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/ads"
              element={
                <ProtectedRoute requireCMSManager fdSection="cms">
                  <Ads />
                </ProtectedRoute>
              }
            />
            {/* SAM-1095: article and ad detail are full pages (they replace the drawers) and own their shell, like the
                event editor. Static segments (new, preview) rank above the :id routes. */}
            <Route
              path="/settings/articles/new"
              element={
                <ProtectedRoute requireCMSManager fdSection="cms" fdAction="create">
                  <ArticleEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/articles/:articleId"
              element={
                <ProtectedRoute requireCMSManager fdSection="cms">
                  <ArticleEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/articles/:articleId/preview"
              element={
                <ProtectedRoute requireCMSManager fdSection="cms">
                  <ArticlePreviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/ads/new"
              element={
                <ProtectedRoute requireCMSManager fdSection="cms" fdAction="create">
                  <AdEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/ads/preview"
              element={
                <ProtectedRoute requireCMSManager fdSection="cms">
                  <AdsPreviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/ads/:adId"
              element={
                <ProtectedRoute requireCMSManager fdSection="cms">
                  <AdEditorPage />
                </ProtectedRoute>
              }
            />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute requireTenantSettings>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/tenant"
                element={
                  <ProtectedRoute requireTenantSettings>
                    <TenantSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/merchant"
                element={
                  <ProtectedRoute requireXhinolaAdmin>
                    <MerchantConfig />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/roles"
                element={
                  <ProtectedRoute requireRolesManager>
                    <Roles />
                  </ProtectedRoute>
                }
              />
              {/* App users and admin users now live under Users — keep old links working. */}
              <Route path="/settings/users" element={<Navigate to="/users" replace />} />
              <Route path="/settings/admin-users" element={<Navigate to="/users?tab=admin-users" replace />} />
              <Route
                path="/settings/gatekeeper-users"
                element={
                  <ProtectedRoute requireAdmin>
                    <GatekeeperUsers />
                  </ProtectedRoute>
                }
              />
              {/* SAM-946 — the tenant's external and internal gates: tenant admin and Xhinola only (isAdmin). */}
              <Route
                path="/settings/gates"
                element={
                  <ProtectedRoute requireAdmin>
                    <GatesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/gates/new"
                element={
                  <ProtectedRoute requireAdmin>
                    <GateFormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/gates/:id"
                element={
                  <ProtectedRoute requireAdmin>
                    <GateFormPage />
                  </ProtectedRoute>
                }
              />
              {/* SAM-946 — the tenant's zone catalog: tenant admin and Xhinola only; off until the zones migration is live. */}
              {ZONES_ENABLED && (
                <>
                  <Route path="/settings/zones" element={<ProtectedRoute requireAdmin><ZonesPage /></ProtectedRoute>} />
                  <Route path="/settings/zones/new" element={<ProtectedRoute requireAdmin><ZoneFormPage /></ProtectedRoute>} />
                  <Route path="/settings/zones/:id" element={<ProtectedRoute requireAdmin><ZoneFormPage /></ProtectedRoute>} />
                </>
              )}
              <Route
                path="/settings/invitation-types"
                element={
                  <ProtectedRoute requireRolesManager>
                    <InvitationTypes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/permissions"
                element={
                  <ProtectedRoute requireAdmin>
                    <PermissionGroups />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/push-notifications"
                element={
                  <ProtectedRoute requireAdmin fdSection="notifications">
                    <PushNotifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/push-notifications/new"
                element={
                  <ProtectedRoute requireAdmin fdSection="notifications" fdAction="create">
                    <PushNotificationCompose />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/notification-templates"
                element={
                  <ProtectedRoute requireAdmin fdSection="notifications">
                    <NotificationTemplates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/eventsad/list"
                element={
                  <ProtectedRoute requireEventDashboardViewer fdSection="events">
                    <EventsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/eventsad/access-settings"
                element={
                  <ProtectedRoute requireEventsManager>
                    <AccessSettings />
                  </ProtectedRoute>
                }
              />
              {/* SAM-714 — full-page event editor + preview (replaces the EventsList drawer).
                  Not wrapped in TableContent: like ServiceEditorPage, they manage their own
                  full-height shell. "/eventsad/new" and "/eventsad/:eventId" share one
                  component, which treats "new" as create mode. */}
              <Route
                path="/eventsad/new"
                element={
                  <ProtectedRoute requireEventsManager fdSection="events" fdAction="create">
                    <EventEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/eventsad/:eventId"
                element={
                  <ProtectedRoute requireEventsManager fdSection="events">
                    <EventEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/eventsad/:eventId/preview"
                element={
                  <ProtectedRoute requireEventsManager fdSection="events">
                    <EventPreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/eventsad/:eventId/dashboard"
                element={
                  <ProtectedRoute requireEventDashboardViewer fdSection="events">
                    <EventDashboard />
                  </ProtectedRoute>
                }
              />
              {/* SAM-1309 (U30): a route that does not exist (or /login once signed in) goes to the role's first screen, not a blank page. */}
              <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </PhoneAccessGate>
        </Layout>
        </>
      ) : (
        <LoginPage onSignIn={handleSignIn} errorMessage={authError} />
      )}
      {/* Dev-only (no-ops in production, see @tanstack/react-query-devtools's own NODE_ENV
          check) — default `buttonPosition` is bottom-right, which sits on top of every page's
          bottom-right sticky action bar (e.g. ServiceEditorPage's Save button). Moved to
          bottom-left so it never covers in-app controls. */}
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </div>
  );
};
